import { ComponentProps, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components, ExtraProps } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import MarkdownTable from './MarkdownTable';
import { Box, Link, Typography } from '@mui/material';
import { MarkdownRendererProps } from 'src/types/markdown';
import {
  MARKDOWN_BLOCKQUOTE_SX,
  MARKDOWN_CODE_BLOCK_SX,
  MARKDOWN_ELEMENT_BLOCKQUOTE,
  MARKDOWN_ELEMENT_CODE,
  MARKDOWN_ELEMENT_EMPHASIS,
  MARKDOWN_ELEMENT_HEADING_1,
  MARKDOWN_ELEMENT_HEADING_2,
  MARKDOWN_ELEMENT_HEADING_3,
  MARKDOWN_ELEMENT_HEADING_4,
  MARKDOWN_ELEMENT_HEADING_5,
  MARKDOWN_ELEMENT_HEADING_6,
  MARKDOWN_ELEMENT_LINK,
  MARKDOWN_ELEMENT_LIST_ITEM,
  MARKDOWN_ELEMENT_ORDERED_LIST,
  MARKDOWN_ELEMENT_PARAGRAPH,
  MARKDOWN_ELEMENT_PRE,
  MARKDOWN_ELEMENT_STRONG,
  MARKDOWN_ELEMENT_UNORDERED_LIST,
  MARKDOWN_HEADING_VARIANT_MAP,
  MARKDOWN_INLINE_CODE_SX,
  MARKDOWN_LINK_REL,
  MARKDOWN_LINK_TARGET,
  MARKDOWN_LIST_ITEM_SX,
  MARKDOWN_LIST_SX,
  MARKDOWN_PARAGRAPH_SX,
} from 'src/constants/markdown';

const DEFAULT_MARKDOWN_VARIANT: MarkdownRendererProps['variant'] = 'body2';
const DEFAULT_MARKDOWN_COLOR: MarkdownRendererProps['color'] = 'text.secondary';

type HeadingProps = ComponentProps<'h1'> & ExtraProps;

const renderHeading = (
  variantKey: string,
  color: MarkdownRendererProps['color'],
): ((props: HeadingProps) => JSX.Element) => {
  return ({ children }) => {
    return (
      <Typography
        variant={MARKDOWN_HEADING_VARIANT_MAP[variantKey]}
        color={color}
        sx={MARKDOWN_PARAGRAPH_SX}
        gutterBottom
      >
        {children}
      </Typography>
    );
  };
};

const buildHeadingComponents = (
  color: MarkdownRendererProps['color'],
): Components => {
  return {
    [MARKDOWN_ELEMENT_HEADING_1]: renderHeading(
      MARKDOWN_ELEMENT_HEADING_1,
      color,
    ),
    [MARKDOWN_ELEMENT_HEADING_2]: renderHeading(
      MARKDOWN_ELEMENT_HEADING_2,
      color,
    ),
    [MARKDOWN_ELEMENT_HEADING_3]: renderHeading(
      MARKDOWN_ELEMENT_HEADING_3,
      color,
    ),
    [MARKDOWN_ELEMENT_HEADING_4]: renderHeading(
      MARKDOWN_ELEMENT_HEADING_4,
      color,
    ),
    [MARKDOWN_ELEMENT_HEADING_5]: renderHeading(
      MARKDOWN_ELEMENT_HEADING_5,
      color,
    ),
    [MARKDOWN_ELEMENT_HEADING_6]: renderHeading(
      MARKDOWN_ELEMENT_HEADING_6,
      color,
    ),
  };
};

export const MarkdownRenderer = ({
  content,
  color = DEFAULT_MARKDOWN_COLOR,
  variant = DEFAULT_MARKDOWN_VARIANT,
  renderImages = true,
}: MarkdownRendererProps) => {
  const headingComponents = useMemo(() => {
    return buildHeadingComponents(color);
  }, [color]);

  const components: Components = useMemo(() => {
    return {
      ...headingComponents,
      img: ({ src, alt, title }) =>
        renderImages ? (
          <img src={src} alt={alt ?? ''} title={title} />
        ) : (
          <Typography component="span" variant={variant} color={color}>
            {alt}
          </Typography>
        ),
      table: ({ node, ...props }) => {
        // The renderer AST node is not a native table attribute.
        void node;

        return <MarkdownTable {...props} />;
      },
      [MARKDOWN_ELEMENT_PARAGRAPH]: ({ children }) => {
        return (
          <Typography
            variant={variant}
            color={color}
            sx={MARKDOWN_PARAGRAPH_SX}
          >
            {children}
          </Typography>
        );
      },
      [MARKDOWN_ELEMENT_STRONG]: ({ children }) => {
        return (
          <Typography
            component="span"
            variant={variant}
            color={color}
            fontWeight="bold"
          >
            {children}
          </Typography>
        );
      },
      [MARKDOWN_ELEMENT_EMPHASIS]: ({ children }) => {
        return (
          <Typography
            component="span"
            variant={variant}
            color={color}
            fontStyle="italic"
          >
            {children}
          </Typography>
        );
      },
      [MARKDOWN_ELEMENT_LINK]: ({ href, children }) => {
        // ReactMarkdown removes unsafe destinations. Do not turn the remaining
        // label into an empty-href link that reloads the current page.
        if (!href) {
          return <span>{children}</span>;
        }

        return (
          <Link
            href={href}
            target={MARKDOWN_LINK_TARGET}
            rel={MARKDOWN_LINK_REL}
            color={color}
            underline="hover"
          >
            {children}
          </Link>
        );
      },
      [MARKDOWN_ELEMENT_UNORDERED_LIST]: ({ children }) => {
        return (
          <Box component="ul" sx={MARKDOWN_LIST_SX}>
            {children}
          </Box>
        );
      },
      [MARKDOWN_ELEMENT_ORDERED_LIST]: ({ children }) => {
        return (
          <Box component="ol" sx={MARKDOWN_LIST_SX}>
            {children}
          </Box>
        );
      },
      [MARKDOWN_ELEMENT_LIST_ITEM]: ({ children }) => {
        return (
          <Box component="li" sx={MARKDOWN_LIST_ITEM_SX}>
            {children}
          </Box>
        );
      },
      [MARKDOWN_ELEMENT_BLOCKQUOTE]: ({ children }) => {
        return (
          <Box component="blockquote" sx={MARKDOWN_BLOCKQUOTE_SX}>
            {children}
          </Box>
        );
      },
      [MARKDOWN_ELEMENT_PRE]: ({ children }) => {
        return (
          <Box component="pre" sx={MARKDOWN_CODE_BLOCK_SX}>
            {children}
          </Box>
        );
      },
      [MARKDOWN_ELEMENT_CODE]: ({
        inline,
        children,
      }: ComponentProps<'code'> & ExtraProps & { inline?: boolean }) => {
        if (inline) {
          return (
            <Box component="code" sx={MARKDOWN_INLINE_CODE_SX}>
              {children}
            </Box>
          );
        }

        return <Box component="code">{children}</Box>;
      },
    };
  }, [color, headingComponents, variant, renderImages]);

  const trimmedContent = content.trim();

  if (trimmedContent.length === 0) {
    return null;
  }

  return (
    <Box>
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
        {trimmedContent}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownRenderer;
